import { useEffect } from "react";
import confetti from "canvas-confetti";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LEVELS } from "@/lib/gamification";
import { useLanguage } from "@/contexts/LanguageContext";

interface LevelUpModalProps {
  open: boolean;
  onClose: () => void;
  newLevel: number;
  calmMode?: boolean;
}

export const LevelUpModal = ({ open, onClose, newLevel, calmMode = false }: LevelUpModalProps) => {
  const levelInfo = LEVELS.find((l) => l.level === newLevel);
  const { t } = useLanguage();

  useEffect(() => {
    if (open && !calmMode) {
      // Trigger confetti
      const duration = 2000;
      const end = Date.now() + duration;

      const colors = ['#10b981', '#34d399', '#6ee7b7'];

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors,
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors,
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      })();
    }
  }, [open, calmMode]);

  if (!levelInfo) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="sr-only">
          <DialogTitle>{t('levelUp.title')}</DialogTitle>
          <DialogDescription>{t('levelUp.subtitle')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center justify-center text-center space-y-4 py-6">
          <div className="text-6xl">🌱</div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-foreground">
              {t('levelUp.title')}
            </h2>
            <p className="text-lg font-medium text-primary">
              {t('gamification.level', { level: levelInfo.level })} - {levelInfo.name}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('levelUp.subtitle')}
            </p>
          </div>
          <Button onClick={onClose} className="mt-4">
            {t('gamification.continue')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
